import { uniqBy } from 'lodash';
import { createAxios } from '../lib/axiosFactory';
import { ActionResponse } from '../lib/interfaces/actionResponse';
import { CreditLogExport } from '../lib/interfaces/creditLogExport';
import { errorParser } from '../lib/response/errorParser';
import { VuexLocalStorage } from '../lib/vuexLocalStorage';
import { safeJsonParse } from '../lib/utilities/json';

const storage = new VuexLocalStorage('_CREDIT_LOG_');

export const state = {
  pendingExports: (safeJsonParse(storage.get('pendingExports')) ||
    []) as CreditLogExport[],
  completedExports: (safeJsonParse(storage.get('completedExports')) ||
    []) as CreditLogExport[]
};

export const mutations = {
  setPendingExports(
    state: any,
    { pendingExports }: { pendingExports: CreditLogExport[] }
  ) {
    storage.set(
      state,
      { pendingExports: state.pendingExports },
      'pendingExports',
      true
    );
  },
  setCompletedExports(
    state: any,
    { completedExports }: { completedExports: CreditLogExport[] }
  ) {
    storage.set(
      state,
      { completedExports: state.completedExports },
      'completedExports',
      true
    );
  },
  addPendingExports(
    state: any,
    { creditLogExport }: { creditLogExport: CreditLogExport }
  ): void {
    state.pendingExports = [creditLogExport, ...state.pendingExports];
    state.pendingExports = uniqBy(
      state.pendingExports,
      (d: CreditLogExport) => d?.id
    );
    storage.set(
      state,
      { pendingExports: state.pendingExports },
      'pendingExports',
      true
    );
  },
  addCompletedExports(
    state: any,
    { creditLogExport }: { creditLogExport: CreditLogExport }
  ): void {
    state.pendingExports = state.pendingExports.filter(
      d => d.id !== creditLogExport.id
    );
    state.completedExports = [creditLogExport, ...state.completedExports];
    state.completedExports = uniqBy(
      state.completedExports,
      (d: CreditLogExport) => d?.id
    );
    storage.set(
      state,
      { pendingExports: state.pendingExports },
      'pendingExports',
      true
    );
    storage.set(
      state,
      { completedExports: state.completedExports },
      'completedExports',
      true
    );
  },
  clearPendingExports(state: any): void {
    state.pendingExports = [];
    storage.set(
      state,
      { pendingExports: state.pendingExports },
      'pendingExports',
      true
    );
  },
  clearCompletedExports(state: any): void {
    state.completedExports = [];
    storage.set(
      state,
      { completedExports: state.completedExports },
      'completedExports',
      true
    );
  }
};

export const actions = {
  async createCreditLogExport(
    { commit, rootState },
    payload: any
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios().post(
        '/backend/credit-log-exports-create',
        {
          data: {
            type: 'creditLogExports',
            attributes: payload
          }
        }
      );

      const creditLogExport = {
        id: response.data.data.id,
        ...response.data.data.attributes
      };
      commit('addPendingExports', { creditLogExport });

      return { success: true, errors: {}, body: response.data.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async submitCreditCorrection(
    context: any,
    { payload }: any
  ): Promise<ActionResponse> {
    try {
      const requestBody = {
        data: {
          type: 'creditsCorrection',
          attributes: payload
        }
      };

      const response = await createAxios().patch(
        'backend/mobiz-admin-credits-correction-create',
        requestBody
      );

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  }
};

export const getters = {
  hasPendingExports: (s: typeof state): boolean => {
    return !!s.pendingExports?.length;
  }
};
